// =============================================================
// Catalogue des véhicules : recherche des marques et des modèles, tolérante aux
// fautes de frappe et aux noms écrits au son (« Toyota allander » → Toyota
// Highlander). Les fonctions reçoivent le catalogue en paramètre : le web le
// charge à la demande (il pèse quelques dizaines de Ko), l'API l'importe.
// Le nom officiel retenu est celui qui s'écrit ensuite dans la fiche, le
// rendez-vous et les textos ; une saisie libre reste toujours possible.
// =============================================================

export interface VehicleCatalogModel {
  /** Nom officiel, tel qu'il s'écrit dans les fiches et les messages. */
  name: string;
  /** Première année-modèle connue (absente = inconnue). */
  from?: number;
  /** Dernière année-modèle connue (absente = toujours produit ou inconnue). */
  to?: number;
  /** Autres écritures courantes, cherchées comme le nom. */
  aliases?: string[];
}

export interface VehicleCatalogMake {
  name: string;
  aliases?: string[];
  models: VehicleCatalogModel[];
}

export type VehicleCatalog = readonly VehicleCatalogMake[];

export interface VehicleMakeMatch {
  make: VehicleCatalogMake;
  score: number;
}

export interface VehicleModelMatch {
  make: VehicleCatalogMake;
  model: VehicleCatalogModel;
  score: number;
  /** Année demandée couverte par le modèle : true, false, ou null si l'une des deux est inconnue. */
  yearMatch: boolean | null;
}

/** Correspondance d'une fiche existante avec le catalogue (proposition de correction). */
export interface VehicleCatalogSuggestion {
  make: string;
  model: string;
  /** La fiche porte déjà exactement ces noms (casse et ponctuation comprises). */
  exact: boolean;
  score: number;
}

/** Score à partir duquel une correspondance approchée est proposée. */
export const VEHICLE_MATCH_MIN_SCORE = 0.5;
/** Score à partir duquel une fiche existante se voit proposer une correction. */
export const VEHICLE_SUGGESTION_MIN_SCORE = 0.6;

/** Minuscules, sans accents ni ponctuation, espaces simples. */
export function normalizeVehicleText(value: string): string {
  return value
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, " ")
    .trim();
}

function compact(value: string): string {
  return normalizeVehicleText(value).replace(/ /g, "");
}

/**
 * Clé « au son » : rapproche les écritures phonétiques fréquentes au Québec
 * (h muet, « igh » anglais, finales en -eur, lettres doublées, c/k/qu, y/i…).
 * Les deux côtés de la comparaison passent par la même clé.
 */
export function phoneticVehicleKey(value: string): string {
  let s = compact(value);
  if (!s) return s;
  s = s
    .replace(/igh/g, "ai")
    .replace(/ph/g, "f")
    .replace(/s?ch|sh/g, "X")
    .replace(/x/g, "ks")
    .replace(/ck|qu|q/g, "k")
    .replace(/c(?=[eiy])/g, "s")
    .replace(/c/g, "k")
    .replace(/wr/g, "r")
    .replace(/w/g, "v")
    .replace(/y/g, "i")
    .replace(/z/g, "s")
    .replace(/eur/g, "er")
    .replace(/air$/g, "er")
    .replace(/eau|au/g, "o")
    .replace(/ou|oo/g, "u")
    .replace(/ai|ei/g, "e")
    .replace(/ee|ea/g, "i")
    .replace(/en/g, "an")
    .replace(/em/g, "am")
    .replace(/h/g, "")
    .replace(/gue$/g, "g")
    .replace(/(.)\1+/g, "$1");
  // E muet final (« Escape » se dit et s'écrit souvent « Escap »).
  if (s.length > 3) s = s.replace(/e$/, "");
  return s;
}

/** Distance d'édition avec transposition de deux lettres voisines (OSA). */
function editDistance(a: string, b: string): number {
  if (a === b) return 0;
  if (!a.length) return b.length;
  if (!b.length) return a.length;
  const rows = a.length + 1;
  const cols = b.length + 1;
  const d: number[][] = Array.from({ length: rows }, () => new Array<number>(cols).fill(0));
  for (let i = 0; i < rows; i++) d[i]![0] = i;
  for (let j = 0; j < cols; j++) d[0]![j] = j;
  for (let i = 1; i < rows; i++) {
    for (let j = 1; j < cols; j++) {
      const cost = a[i - 1] === b[j - 1] ? 0 : 1;
      let v = Math.min(d[i - 1]![j]! + 1, d[i]![j - 1]! + 1, d[i - 1]![j - 1]! + cost);
      if (i > 1 && j > 1 && a[i - 1] === b[j - 2] && a[i - 2] === b[j - 1]) {
        v = Math.min(v, d[i - 2]![j - 2]! + 1);
      }
      d[i]![j] = v;
    }
  }
  return d[rows - 1]![cols - 1]!;
}

function similarity(a: string, b: string): number {
  if (!a || !b) return 0;
  return 1 - editDistance(a, b) / Math.max(a.length, b.length);
}

/**
 * Score de 0 à 1 entre une saisie et un nom. Une correspondance exacte vaut 1,
 * un début de nom ou de mot plus de 0,85 ; une correspondance approchée reste
 * sous 0,85 pour ne jamais passer devant une saisie juste.
 */
export function vehicleNameScore(query: string, candidate: string): number {
  const qn = normalizeVehicleText(query);
  const cn = normalizeVehicleText(candidate);
  const q = qn.replace(/ /g, "");
  const c = cn.replace(/ /g, "");
  if (!q || !c) return 0;
  if (q === c) return 1;
  if (c.startsWith(q)) return 0.9 + 0.08 * (q.length / c.length);
  // Un mot du nom commence par la saisie : « cherokee » trouve aussi Grand Cherokee.
  if (cn.split(" ").some((word) => word.startsWith(q))) return 0.86 + 0.03 * (q.length / c.length);
  // Nom suivi d'une version ou d'une finition : « highlander xle ».
  if (c.length >= 3 && q.startsWith(c)) return 0.86;
  if (q.length >= 2 && c.includes(q)) return 0.8;
  if (q.length < 3) return 0;
  const pq = phoneticVehicleKey(qn);
  const pc = phoneticVehicleKey(cn);
  const direct = similarity(q, c);
  const phonetic = similarity(pq, pc);
  // Saisie en cours de frappe : comparer au début du nom de même longueur.
  // Une clé phonétique trop courte (« hond » → « ond ») rapprocherait n'importe quoi.
  const partial =
    q.length >= 4 && q.length < c.length
      ? Math.max(
          similarity(q, c.slice(0, q.length)),
          pq.length >= 4 ? similarity(pq, pc.slice(0, pq.length)) : 0,
        ) * 0.95
      : 0;
  return Math.max(direct, phonetic, partial) * 0.84;
}

function bestScore(query: string, names: readonly string[]): number {
  let best = 0;
  for (const name of names) best = Math.max(best, vehicleNameScore(query, name));
  return best;
}

/** Marque du catalogue désignée exactement (nom ou variante, sans accents ni casse). */
export function findVehicleMake(catalog: VehicleCatalog, name: string): VehicleCatalogMake | null {
  const key = compact(name);
  if (!key) return null;
  return (
    catalog.find((make) => compact(make.name) === key) ??
    catalog.find((make) => (make.aliases ?? []).some((alias) => compact(alias) === key)) ??
    null
  );
}

/** Le modèle couvre-t-il l'année ? null si l'année ou les bornes sont inconnues. */
export function vehicleModelYearMatch(
  model: VehicleCatalogModel,
  year: number | null | undefined,
): boolean | null {
  if (!year || (!model.from && !model.to)) return null;
  if (model.from && year < model.from) return false;
  if (model.to && year > model.to) return false;
  return true;
}

/** Marques triées par pertinence ; sans saisie, toutes les marques dans l'ordre alphabétique. */
export function searchVehicleMakes(
  catalog: VehicleCatalog,
  query: string,
  limit = 8,
): VehicleMakeMatch[] {
  if (!normalizeVehicleText(query)) {
    return [...catalog]
      .sort((a, b) => a.name.localeCompare(b.name, "fr"))
      .slice(0, limit)
      .map((make) => ({ make, score: 0 }));
  }
  return catalog
    .map((make) => ({ make, score: bestScore(query, [make.name, ...(make.aliases ?? [])]) }))
    .filter((m) => m.score >= VEHICLE_MATCH_MIN_SCORE)
    .sort((a, b) => b.score - a.score || a.make.name.localeCompare(b.make.name, "fr"))
    .slice(0, limit);
}

/** Retire le nom de la marque répété en tête de la saisie du modèle (« mazda 3 » → « 3 »). */
function stripMakePrefix(make: VehicleCatalogMake, query: string): string {
  const q = normalizeVehicleText(query);
  for (const name of [make.name, ...(make.aliases ?? [])]) {
    const n = normalizeVehicleText(name);
    if (n && q.startsWith(`${n} `)) return q.slice(n.length + 1);
  }
  return q;
}

function rankModels(
  make: VehicleCatalogMake,
  query: string,
  year: number | null | undefined,
): VehicleModelMatch[] {
  const q = normalizeVehicleText(query);
  const stripped = stripMakePrefix(make, query);
  return make.models
    .map((model) => {
      const names = [model.name, ...(model.aliases ?? [])];
      const score = Math.max(bestScore(q, names), stripped !== q ? bestScore(stripped, names) : 0);
      const yearMatch = vehicleModelYearMatch(model, year);
      // Une année incompatible fait reculer le modèle sans le retirer : les
      // bornes du catalogue peuvent manquer un millésime de transition.
      return { make, model, score: yearMatch === false ? score - 0.12 : score, yearMatch };
    })
    .filter((m) => m.score >= VEHICLE_MATCH_MIN_SCORE);
}

/**
 * Modèles d'une marque. Sans saisie : les modèles de l'année (tous si l'année
 * est inconnue), dans l'ordre alphabétique. Avec saisie : par pertinence.
 * Une marque hors catalogue ne propose rien (saisie libre).
 */
export function searchVehicleModels(
  catalog: VehicleCatalog,
  makeName: string,
  query: string,
  year?: number | null,
  limit = 10,
): VehicleModelMatch[] {
  const make = findVehicleMake(catalog, makeName);
  if (!make) return [];
  if (!normalizeVehicleText(query)) {
    return make.models
      .map((model) => ({ make, model, score: 0, yearMatch: vehicleModelYearMatch(model, year) }))
      .filter((m) => m.yearMatch !== false)
      .sort((a, b) => a.model.name.localeCompare(b.model.name, "fr"))
      .slice(0, limit);
  }
  return rankModels(make, query, year)
    .sort((a, b) => b.score - a.score || a.model.name.localeCompare(b.model.name, "fr"))
    .slice(0, limit);
}

/**
 * Recherche d'un véhicule en une seule saisie (« toyota allander », « crv 2018 »).
 * La marque reconnue en tête restreint les modèles ; sinon tout le catalogue est parcouru.
 */
export function searchVehicles(
  catalog: VehicleCatalog,
  query: string,
  year?: number | null,
  limit = 8,
): VehicleModelMatch[] {
  const words = normalizeVehicleText(query).split(" ").filter(Boolean);
  const yearWord = words.find((w) => /^(19|20)\d\d$/.test(w));
  const effectiveYear = year ?? (yearWord ? Number(yearWord) : null);
  const rest = words.filter((w) => w !== yearWord);
  if (!rest.length) return [];
  // Marque en un ou deux mots (« land rover », « mercedes benz »), la plus longue d'abord.
  for (const size of [2, 1]) {
    if (rest.length < size) continue;
    const head = rest.slice(0, size).join(" ");
    // Deux mots ne désignent une marque que si elle s'écrit elle-même en deux
    // mots : « toyota allander » est une marque suivie d'un modèle.
    const candidates =
      size === 1
        ? catalog
        : catalog.filter((make) =>
            [make.name, ...(make.aliases ?? [])].some((n) => normalizeVehicleText(n).includes(" ")),
          );
    const [best] = searchVehicleMakes(candidates, head, 1);
    if (best && best.score >= 0.85) {
      const tail = rest.slice(size).join(" ");
      if (!tail) {
        return searchVehicleModels(catalog, best.make.name, "", effectiveYear, limit);
      }
      return rankModels(best.make, tail, effectiveYear)
        .sort((a, b) => b.score - a.score)
        .slice(0, limit);
    }
  }
  const text = rest.join(" ");
  return catalog
    .flatMap((make) => rankModels(make, text, effectiveYear))
    .sort((a, b) => b.score - a.score || a.model.name.localeCompare(b.model.name, "fr"))
    .slice(0, limit);
}

/**
 * Correspondance d'une marque et d'un modèle déjà saisis avec le catalogue.
 * Sert à PROPOSER une correction sur une ancienne fiche (jamais à corriger en
 * silence) et à retrouver le nom officiel d'une saisie. null si rien de sûr.
 */
export function matchVehicleCatalog(
  catalog: VehicleCatalog,
  makeName: string,
  modelName: string,
  year?: number | null,
): VehicleCatalogSuggestion | null {
  if (!normalizeVehicleText(makeName) || !normalizeVehicleText(modelName)) return null;
  let make = findVehicleMake(catalog, makeName);
  let makeScore = make ? 1 : 0;
  if (!make) {
    const [best] = searchVehicleMakes(catalog, makeName, 1);
    if (!best || best.score < VEHICLE_SUGGESTION_MIN_SCORE) return null;
    make = best.make;
    makeScore = best.score;
  }
  const [best] = rankModels(make, modelName, year).sort((a, b) => b.score - a.score);
  if (!best || best.score < VEHICLE_SUGGESTION_MIN_SCORE) return null;
  const score = Math.min(makeScore, best.score);
  if (score < VEHICLE_SUGGESTION_MIN_SCORE) return null;
  // Une fiche plus précise que le catalogue (« Highlander Hybride XLE ») n'est
  // pas une faute : on ne propose jamais d'en retirer des mots.
  const typed = stripMakePrefix(make, modelName).split(" ");
  const official = normalizeVehicleText(best.model.name).split(" ");
  const moreSpecific =
    typed.length > official.length && official.every((word, i) => typed[i] === word);
  if (moreSpecific) return null;
  return {
    make: make.name,
    model: best.model.name,
    exact: makeName.trim() === make.name && modelName.trim() === best.model.name,
    score,
  };
}

/**
 * Faut-il proposer une correction ? Seulement quand le catalogue reconnaît le
 * véhicule sous un autre nom ; une écriture déjà officielle ou un véhicule
 * inconnu (saisie libre) ne déclenchent rien.
 */
export function vehicleCorrection(
  catalog: VehicleCatalog,
  makeName: string,
  modelName: string,
  year?: number | null,
): VehicleCatalogSuggestion | null {
  const match = matchVehicleCatalog(catalog, makeName, modelName, year);
  return match && !match.exact ? match : null;
}

/**
 * Ressemblance de 0 à 1 entre deux mots (orthographe ou son) : sert aussi à
 * vérifier un nom propre dicté au téléphone (« Clin » pour « Clain »).
 */
export function wordSimilarity(a: string, b: string): number {
  const x = compact(a);
  const y = compact(b);
  if (!x || !y) return 0;
  if (x === y) return 1;
  return Math.max(similarity(x, y), similarity(phoneticVehicleKey(x), phoneticVehicleKey(y)));
}
